// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  Share,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';

import GoffyButton from '@/components/GoffyButton';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { resolveFriendInviteLink } from '@/services/friendInviteLink';
import appColors, { alpha } from '@/theme/colors';

type FriendInviteQrButtonProps = {
  style?: StyleProp<ViewStyle>;
};

/** Refresh this long before the token expires, so a code on screen is always scannable. */
const REFRESH_LEAD_MS = 20_000;

/**
 * "Connect via QR" on the friends screen. Shows a short-lived code for someone standing next to you
 * to scan with their phone's camera app.
 *
 * The code rotates itself while the modal is open. That is what keeps the mutual add safe: a token
 * only lives a couple of minutes, so a photograph of this screen taken from across the room is
 * useless by the time anyone tries it. Unlike the tour share modal there is no short join code —
 * these tokens are signed, so a typeable prefix would mean nothing.
 */
export function FriendInviteQrButton({ style }: FriendInviteQrButtonProps) {
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openRef = useRef(false);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const loadLink = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resolved = await resolveFriendInviteLink({ apiBaseUrl, authToken: user?.token });
      if (!openRef.current) {
        return;
      }
      setLink(resolved.link);
      setExpiresAt(resolved.expiresAt);
    } catch {
      if (openRef.current) {
        setError(t('friendInvite.loadError'));
      }
    } finally {
      if (openRef.current) {
        setLoading(false);
      }
    }
  }, [apiBaseUrl, t, user?.token]);

  // Rotate shortly before the current token lapses, but only while the modal is actually on screen.
  useEffect(() => {
    clearRefreshTimer();
    if (!open || !expiresAt) {
      return;
    }

    const expiryMs = Date.parse(expiresAt);
    if (Number.isNaN(expiryMs)) {
      return;
    }

    const delay = Math.max(1_000, expiryMs - Date.now() - REFRESH_LEAD_MS);
    refreshTimerRef.current = setTimeout(() => {
      void loadLink();
    }, delay);

    return clearRefreshTimer;
  }, [clearRefreshTimer, expiresAt, loadLink, open]);

  useEffect(() => clearRefreshTimer, [clearRefreshTimer]);

  const handleOpen = () => {
    openRef.current = true;
    setOpen(true);
    setLink(null);
    setExpiresAt(null);
    void loadLink();
  };

  const handleClose = () => {
    openRef.current = false;
    clearRefreshTimer();
    setOpen(false);
    setLink(null);
    setExpiresAt(null);
    setError(null);
  };

  const handleShare = async () => {
    if (!link) {
      return;
    }
    const title = t('friendInvite.shareAction');
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        const nav = navigator as {
          share?: (data: { title?: string; url?: string }) => Promise<void>;
          clipboard?: { writeText?: (value: string) => Promise<void> };
        };
        if (typeof nav.share === 'function') {
          await nav.share({ title, url: link });
          return;
        }
        if (nav.clipboard?.writeText) {
          await nav.clipboard.writeText(link);
          setError(null);
          return;
        }
        throw new Error('share unsupported');
      }

      await Share.share({ title, message: link });
    } catch (err) {
      const message = err instanceof Error ? err.message.toLowerCase() : '';
      if (message.includes('cancel')) {
        return; // user dismissed the share sheet
      }
      setError(t('friendInvite.shareError'));
    }
  };

  return (
    <View style={[styles.row, style]}>
      <GoffyButton
        label={t('friends.connectViaQr')}
        onPress={handleOpen}
        tone="accent"
        size="compact"
        testID="friend-invite-open"
        accessibilityLabel={t('friends.connectViaQr')}
      />

      <Modal transparent visible={open} animationType="fade" onRequestClose={handleClose}>
        <Pressable style={styles.overlay} onPress={handleClose}>
          <Pressable style={styles.card} onPress={() => null}>
            <Text style={styles.title}>{t('friendInvite.showTitle')}</Text>
            <Text style={styles.subtitle}>{t('friendInvite.showSubtitle')}</Text>
            <View style={styles.codeCard}>
              {loading ? <ActivityIndicator size="small" color={appColors.accent.primary} /> : null}
              {link ? (
                <View style={styles.qrWrapper} testID="friend-invite-qr">
                  <QRCode value={link} size={260} color={appColors.neutral.black} backgroundColor={appColors.neutral.white} />
                </View>
              ) : null}
              <Text style={styles.expiryNote}>{t('friendInvite.expiryNote')}</Text>
            </View>
            {error ? (
              <Text style={styles.error} testID="friend-invite-error">
                {error}
              </Text>
            ) : null}
            <View style={styles.actions}>
              <GoffyButton
                label={t('friendInvite.shareAction')}
                onPress={() => void handleShare()}
                disabled={!link || loading}
                tone="accent"
                size="compact"
                testID="friend-invite-share"
                style={styles.actionButton}
              />
              <GoffyButton
                label={t('friendInvite.regenerate')}
                onPress={() => void loadLink()}
                disabled={loading}
                tone="dark"
                size="compact"
                testID="friend-invite-regenerate"
                style={styles.actionButton}
              />
              <GoffyButton
                label={t('friendInvite.close')}
                onPress={handleClose}
                tone="dark"
                size="compact"
                testID="friend-invite-close"
                style={styles.actionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

export default FriendInviteQrButton;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    width: '100%',
  },
  overlay: {
    flex: 1,
    backgroundColor: alpha(appColors.surface.tintDeep, 0.22),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    padding: 18,
    gap: 12,
  },
  title: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  subtitle: {
    color: appColors.text.muted,
    fontSize: 14,
    textAlign: 'center',
  },
  codeCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.deep,
    padding: 16,
    gap: 10,
    alignItems: 'center',
  },
  qrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: appColors.neutral.white,
    padding: 12,
  },
  expiryNote: {
    color: appColors.text.muted,
    fontSize: 12,
    textAlign: 'center',
  },
  actions: {
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'flex-start',
  },
  actionButton: {
    width: '100%',
  },
  error: {
    color: appColors.status.errorText,
    marginTop: 4,
  },
});
